import {
  DropDownOption,
  FieldEmitEvent,
  FormCollection,
  FormCollectionSet,
  IDisplayData,
  IPatientEntriesDisplayData,
  IPatientEntryData
} from 'src/interfaces/utility-interfaces';

import { Injectable } from '@angular/core';
import { fhirR4 } from '@smile-cdr/fhirts';
import { ClinicalService } from 'src/app/services/clinical.service';
import { AppConstants } from 'src/constants/constants';
import { BaseInput } from '../shared/models/base-input';
import { DropdownInput } from '../shared/models/dropdown-input';
import { ErrorService } from './error.service';
import { HttpService } from './http.service';
import { InputControlService } from './input-control.service';
import { InputService } from './input.service';
import { UtilService } from './util.service';

@Injectable({
  providedIn: 'root'
})
export class EntryService {

  constructor(
    private httpService: HttpService,
    private utilService: UtilService,
    private inputControlService: InputControlService,
    private errorService: ErrorService,
    private clinicalService: ClinicalService,
    private inputService: InputService
  ) { }

  getUserProfile(profileId: string) {
    const queryParams = 'PractitionerRole?_id=' + profileId;
    return this.httpService.executeGETQueryWithParams(queryParams, true);
  }

  getClinicalEntriesForPatient(patientId: string, accessSites?: string[]) {
    let queryParams = 'ServiceRequest?_include=*&_revinclude=*&_include:iterate=*&subject=' + patientId;
    if (accessSites) {
      queryParams = queryParams + `&performer:Organization=${accessSites.join(',')}`;
    }
    return this.httpService.executeGETQueryWithParams(queryParams, true);
  }

  fetchAllClinicalEntriesForPatient(patientId: string, accessSites?: string[]) {
    return new Promise((resolve, reject) => {
      let returnObj: fhirR4.Bundle;
      this.getClinicalEntriesForPatient(patientId, accessSites).then(async (data: fhirR4.Bundle) => {
        returnObj = data;
        let nextLink = data?.link?.find( lnk => lnk.relation === 'next');
        while (nextLink) {
          const nextUrlParams = '?' + nextLink.url.split('?')[1];
          await this.httpService.executeGETQueryWithParams(nextUrlParams).then((nextpgData: fhirR4.Bundle) => {
                returnObj.entry = returnObj.entry.concat(nextpgData.entry);
                nextLink = nextpgData?.link?.find( lnk => lnk.relation === 'next');
              }, err => {
                reject(err);
              });
        }
        if (returnObj.entry) {
          returnObj.entry = returnObj.entry.filter(entry => {
            if (entry?.resource.resourceType === 'ServiceRequest') {
              const serviceRequest: fhirR4.ServiceRequest = entry.resource as fhirR4.ServiceRequest;
              // Get Service Location
              if (serviceRequest.performer) {
                for (const performer of serviceRequest.performer) {
                  if (performer.type === 'Organization') {
                    const serviceLocationReference = performer.reference;
                    const serviceLocationId = this.utilService.getIdFromReference(serviceLocationReference);
                    if (accessSites.includes(serviceLocationId)) {
                      return entry;
                    }
                  }
                }
              }
            } else {
              return entry;
            }
          });
        }
        resolve(returnObj);
      }, err => {
        reject(err);
      });
    });
  }

  getServiceLocationByWleId(wleId: string): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!wleId) {
        resolve('');
        return;
      }
      const queryParams = 'ServiceRequest?_id=' + wleId;
      this.httpService.executeGETQueryWithParams(queryParams, true).then((data: fhirR4.Bundle) => {
        if (data.entry && data.total > 0) {
          const serviceRequest: fhirR4.ServiceRequest = data.entry[0].resource as fhirR4.ServiceRequest;
          if (serviceRequest.performer) {
            for (const performer of serviceRequest.performer) {
              if (performer.type === 'Organization') {
                resolve(performer.reference);
                return;
              }
            }
          }
        }
        resolve('');
      }, err => {
        reject(err);
      });
    });
  }

  checkWleIdAgainstSites(wleId: string, siteList: string) {
    const queryParams = 'ServiceRequest?_include=*&_revinclude=*&_include:iterate=*&_id=' + wleId + '&performer:Organization=' + siteList;
    return this.httpService.executeGETQueryWithParams(queryParams, true);
  }

  // Extracts entry status from Wait Time QR
  getEntryStatus(entryQR: fhirR4.QuestionnaireResponse): string {
    const entryStatusObj = entryQR.item && entryQR.item.find(elem => elem.linkId === 'entry-status');

    let entryStatus: string;
    if (entryStatusObj) {
      const answerCode = entryStatusObj.answer[0].valueCoding.code;
      if (answerCode === 'O') {
        entryStatus = 'Open';
      } else if (answerCode === 'C') {
        entryStatus = 'Closed';
      }
    } else {
      entryStatus = '-';
    }
    return entryStatus;
  }

  getEntryPriority(entryQR: fhirR4.QuestionnaireResponse): string {
    const entryPriorityObj = entryQR.item && entryQR.item.find(elem => elem.linkId === 'priority-level');
    let priorityLevel: string;
    if (entryPriorityObj) {
      priorityLevel = entryPriorityObj.answer[0].valueString;
    } else {
      priorityLevel = '-';
    }
    return priorityLevel;
  }

  processClinicalEntriesBundle(entries: fhirR4.BundleEntry[], orgName: string): IPatientEntriesDisplayData[] {
    const resultList: IPatientEntriesDisplayData[] = [];
    const serviceRequestList: fhirR4.ServiceRequest[] = [];
    const facilityMap = {};
    const observationList: fhirR4.Observation[] = [];
    entries.forEach(entry => {
      const resource = entry.resource;
      switch (resource.resourceType) {
        case 'ServiceRequest':
          const serviceRequest = resource as fhirR4.ServiceRequest;
          serviceRequestList.push(serviceRequest);
          break;
        case 'Organization':
          const organization = resource as fhirR4.Organization;
          facilityMap[organization.id] = organization;
          break;
        case 'Observation':
          const observation = resource as fhirR4.Observation;
          observationList.push(observation);
          break;
      }
    });
    for (const serviceRequest of serviceRequestList) {
      resultList.push(this.buildClinicalEntryForDisplay(serviceRequest, facilityMap, orgName, observationList));
    }
    return resultList;
  }

  buildClinicalEntryForDisplay(serviceRequest: fhirR4.ServiceRequest,
    facilityMap, profileFacilityName: string, observationList: fhirR4.Observation[]): IPatientEntriesDisplayData {
    const entryForDisplay: IPatientEntriesDisplayData = {};

    // #3571 If an Entry has an identifier, the identifier should be displayed. This is for migrated entries
    if (serviceRequest.identifier) {
      for (const identifier of serviceRequest.identifier) {
        if (identifier.system === AppConstants.ENTRY_IDENTIFIER_SYSTEM) {
          entryForDisplay.identifierId = identifier.value;
        }
      }
    }

    // entry Id
    entryForDisplay.fhirId = serviceRequest.id;

    // If Entry has an identifier, its a migrated entry and WTIS ID is the ID
    // If the Entry doesn't have an identifier, its a DCIS entry and the FHIR ID is the ID
    entryForDisplay.id = entryForDisplay.identifierId ? entryForDisplay.identifierId : 
                                                        entryForDisplay.fhirId;
    entryForDisplay.id = parseInt(entryForDisplay.id);

    // Get service location, SD1, referral date, acceptance date, removal date
    if (serviceRequest.occurrencePeriod) {
      if (serviceRequest.occurrencePeriod.start) {
        entryForDisplay.acceptanceDate = this.utilService.getFormattedDateFromGivenValue(serviceRequest.occurrencePeriod.start);
      }
      if (serviceRequest.occurrencePeriod.end) {
        entryForDisplay.removalDate = this.utilService.getFormattedDateFromGivenValue(serviceRequest.occurrencePeriod.end);
      }
    }

    if (serviceRequest.authoredOn) {
      entryForDisplay.referralDate = this.utilService.getFormattedDateFromGivenValue(serviceRequest.authoredOn);
    }

    if (serviceRequest.status) {
      let status = null;
      if (serviceRequest.status === 'active') {
        status = 'Open';
      } else {
        status = 'Closed';
      }
      entryForDisplay.status = status;
    }

    // Priority Level
    // Observations List contains all the Observations of all Entries at this Facility
    // Filter it down to only contain the Observations related to the SR being processed in this method
    // Then pass that Observation list to fetch Priority Level
    if (serviceRequest.supportingInfo && observationList.length > 0) {
      const serviceRequestObservationIds = [];
      for (const reference of serviceRequest.supportingInfo) {
        const observationId = this.utilService.getIdFromReference(reference.reference);
        serviceRequestObservationIds.push(observationId);
      }
      const serviceRequestObservations = observationList.filter(item => serviceRequestObservationIds.includes(item.id));
      const priorityLevel = this.clinicalService.getValueFromObservationWithGivenCode(AppConstants.PRIORITY_LEVEL_OBSERVATION_CODE,
        serviceRequestObservations) as string;
      if (priorityLevel) {
        entryForDisplay.priorityLevel = priorityLevel;
      }
    }

    if (serviceRequest.performer) {
      for (const performer of serviceRequest.performer) {
        if (performer.type === 'Organization') {
          const organizationID = this.utilService.getIdFromReference(performer.reference);
          let siteName = facilityMap[organizationID] ? facilityMap[organizationID].name : AppConstants.dashUISymbol;
          // tslint:disable-next-line: max-line-length
          const siteNumber = facilityMap[organizationID] ? this.utilService.getIdFromResource(facilityMap[organizationID], 'Organization') : '';
          if (profileFacilityName.toLocaleLowerCase() !== siteName.toLocaleLowerCase()) {
            siteName = `${profileFacilityName} - ${siteName} (${siteNumber})`;
          }
          entryForDisplay.serviceLocation = siteName;
        }
      }
    }

    const serviceCode = this.clinicalService.getCodeFromCodeableConcept(serviceRequest.code,
      AppConstants.serviceDetailCodeSystem) as string;
    const arr: string[] = serviceCode.split('.');
    const serviceDetail1 = arr[2];
    if (serviceDetail1) {
      entryForDisplay.serviceDetail = this.inputService.fullServiceDetail1Options.find(x => x.code === serviceDetail1)?.display  ;
    }
    const serviceDetail2 = arr[3];
    if (serviceDetail2) {
      entryForDisplay.serviceDetail = entryForDisplay.serviceDetail += ' - '
      + this.inputService.fullServiceDetail2Options.find(x => x.code === serviceDetail2)?.display;
    }
    return entryForDisplay;
  }

  buildEntryForEditPrepopulate(entryList: fhirR4.BundleEntry[]): IPatientEntryData {
    let entryForDisplay: IPatientEntryData = {
      QRIDs: {}
    };

    for (const entry of entryList) {
      entryForDisplay = this.processResource(entry.resource, entryForDisplay);
    }

    return entryForDisplay;
  }

  processResource(resource: fhirR4.Resource, entryForDisplay: IPatientEntryData): IPatientEntryData {
    if (resource.resourceType === 'QuestionnaireResponse') {
      const entryQR = resource as fhirR4.QuestionnaireResponse;
      const sectionArray: IDisplayData[] = [];
      const sectionTag = this.utilService.getCodeFromCoding(entryQR.identifier.type.coding,
        'https://corhealth-ontario.ca/entry/system', 'code');

      entryForDisplay.QRIDs[sectionTag] = entryQR.id;
      entryForDisplay.identifierId = entryQR.identifier.value || AppConstants.dashUISymbol;

      if (entryQR.identifier.type.coding[0].code === 'waitTime') {
        entryForDisplay.status = this.getEntryStatus(entryQR);
      }

      if (entryQR.item) {
        for (const item of entryQR.item) {
          const value = this.utilService.processAnswer(item.answer);
          sectionArray.push({
            linkId: item.linkId,
            value
          });
        }
      }
      entryForDisplay[sectionTag] = sectionArray;
    }
    return entryForDisplay;
  }


  patchValueForVentricularEjectionGrade(event: FieldEmitEvent, section: string, formCollection: FormCollectionSet) {
    // Special condition added to patch value for left-ventricular-ejection-fraction-grade
    // according to left-ventricular-ejection-fraction field
    if (event.field.linkId === 'left-ventricular-ejection-fraction') {

      const lvefg = formCollection[section].form.get('left-ventricular-ejection-fraction-grade');

      if (event.newValue) {
        const fraction = +event.newValue;
        if (fraction >= 0 && fraction < 20) {
          lvefg.patchValue('4');
        } else if (fraction >= 20 && fraction <= 34) {
          lvefg.patchValue('3');
        } else if (fraction >= 35 && fraction <= 49) {
          lvefg.patchValue('2');
        } else if (fraction >= 50 && fraction <= 100) {
          lvefg.patchValue('1');
        }
      } else {
        lvefg.reset();
      }
      formCollection[section].data = this.inputControlService.checkDisabledWhen(formCollection[section].form,
        formCollection[section].data);
    }
  }

  crossFormEnableWhenCheck(event: FieldEmitEvent, section: string, formCollection: FormCollectionSet): string {
    // Anytime the value of service detail 2 is changed, the Acceptance and Diagnostics items
    // need to be changed as the values need to be changed simultaneously
    let crossFormChange: string;
    if (section === 'service' && event.field.linkId.startsWith(AppConstants.serviceDetail2LinkId) &&
      !this.utilService.findDependent('service', AppConstants.serviceDetail2LinkId, formCollection)) {
      this.changeFieldValue('referral', AppConstants.referralServiceDetail2, formCollection);
      this.changeFieldValue('diagnostics', AppConstants.diagnosticsServiceDetail2, formCollection);
      this.changeFieldValue('acceptance', AppConstants.acceptanceServiceDetail2, formCollection);
      this.changeFieldValue('diagnostics', AppConstants.diagnosticsPrimaryReferralType, formCollection);
      this.changeFieldValue('diagnostics', AppConstants.diagnosticSecondaryReferralType, formCollection);
      this.changeFieldValue('offlisting', AppConstants.offlistingHistoryofCoronaryArteryBypass, formCollection);
      crossFormChange = 'service detail 2';
    }
    if (section === 'referral' && event.field.linkId.startsWith(AppConstants.primaryReasonReferralTypeLinkId) &&
      !this.utilService.findDependent('referral', AppConstants.primaryReasonReferralTypeLinkId, formCollection)) {
      this.changeFieldValue('diagnostics', AppConstants.diagnosticsPrimaryReferralType, formCollection);
      this.changeFieldValue('offlisting', AppConstants.offlistingPrimaryReferralType, formCollection);
      crossFormChange = 'primary referral type';
    }

    if (section === 'referral' && event.field.linkId.startsWith(AppConstants.secondaryReasonReferralTypeLinkId) &&
      !this.utilService.findDependent('referral', AppConstants.secondaryReasonReferralTypeLinkId, formCollection)) {
      this.changeFieldValue('diagnostics', AppConstants.diagnosticSecondaryReferralType, formCollection);
      crossFormChange = 'secondary referral type';
    }

    if (section === 'diagnostics' && event.field.linkId.startsWith(AppConstants.historyofCoronaryArteryBypassLinkId) &&
      !this.utilService.findDependent('diagnostics', AppConstants.historyofCoronaryArteryBypassLinkId, formCollection)) {
        this.changeFieldValue('offlisting', AppConstants.offlistingHistoryofCoronaryArteryBypass, formCollection);
        crossFormChange = 'history of coronary';
    }

    if (event.newValue) {
      const dropDownItem = event.newValue as DropDownOption;
      if (event.field.label === AppConstants.serviceDetail2Label) {
        this.changeFieldValue('referral', AppConstants.referralServiceDetail2, formCollection, dropDownItem.code);
        this.changeFieldValue('diagnostics', AppConstants.diagnosticsServiceDetail2, formCollection, dropDownItem.code);
        this.changeFieldValue('acceptance', AppConstants.acceptanceServiceDetail2, formCollection, dropDownItem.code);
        crossFormChange = 'service detail 2';
      } else if (event.field.label === AppConstants.primaryReasonReferralTypeLabel) {
        this.changeFieldValue('diagnostics', AppConstants.diagnosticsPrimaryReferralType, formCollection, dropDownItem.code);
        this.changeFieldValue('offlisting', AppConstants.offlistingPrimaryReferralType, formCollection, dropDownItem.code);
        crossFormChange = 'primary referral type';
      } else if (event.field.label === AppConstants.secondaryReasonReferralTypeLabel) {
        this.changeFieldValue('diagnostics', AppConstants.diagnosticSecondaryReferralType, formCollection, dropDownItem.code);
        crossFormChange = 'secondary referral type';
      } else if (event.field.label === AppConstants.historyofCoronaryArteryBypassLabel) {
        this.changeFieldValue('offlisting', AppConstants.offlistingHistoryofCoronaryArteryBypass, formCollection, dropDownItem.code);
        crossFormChange = 'history of coronary';
      }
    }
    if (event.field.linkId === AppConstants.serviceLocationLinkId) {
      const dropDownItemValue = (event.newValue as DropDownOption)?.code || null;
      this.changeFieldValue('diagnostics', AppConstants.diagnosticserviceLocation, formCollection, dropDownItemValue);
      this.changeFieldValue('offlisting', AppConstants.offlistingServiceLocation, formCollection, dropDownItemValue);
    }
    return crossFormChange;
  }


  changeFieldValue(section: string, field: string, formCollection: FormCollectionSet, newValue?: string) {
    const existingValue = formCollection[section].form.get(field)?.value;
    if (newValue) {
      formCollection[section].form.get(field).patchValue(newValue);
    } else {
      formCollection[section].form.get(field).reset();
    }
    if (field === AppConstants.offlistingServiceLocation && (existingValue != newValue)) {
      for (const input of formCollection[section].data) {
        if (input.linkId.startsWith(AppConstants.OffListingProcedureHCPLinkId)
        || input.linkId.startsWith(AppConstants.OffListingProcedureSecondHCPLinkId)) {
          formCollection[section].form.get(input.linkId).reset();
          (input as DropdownInput).options = [];
        }
      }
    }
    formCollection[section].data =
      this.inputControlService.checkEnabledWhen(formCollection[section].form, formCollection[section].data);
    formCollection[section].data = this.inputControlService.checkDisabledWhen(formCollection[section].form, formCollection[section].data);
  }

  addEntryIdToQuestionnaireResponse(questionnaireResponse: fhirR4.QuestionnaireResponse, entryId: string): fhirR4.QuestionnaireResponse {
    const identifier = questionnaireResponse.identifier;
    identifier.value = entryId;
    questionnaireResponse.identifier = identifier;
    return questionnaireResponse;
  }

  findInvalidControls(formCollection: FormCollectionSet, required = false): string[] {
    let invalid = [];
    let countDelayreason = 0;
    let hasRace = true;  // #4186 make this field false again when we need to show race 
    Object.keys(formCollection).forEach((formName: string) => {
      const formSection = formCollection[formName] as FormCollection;
      const controls = formSection.form.controls;
      for (const name in controls) {
        if (required) { // for entry closed status
          if (name.startsWith('race-')) {
            if (controls[name].value) {
              hasRace = true;
            }
          } else if (name.includes('delay-reason') && name !== 'delay-reason' && !controls[name].value) {
            countDelayreason += 1;
            invalid.push(name);
          } else if (name.endsWith('-datetime')) {
            const datetimeField = formCollection.diagnostics.data.find(item => item.linkId === name);
            const isMissingReasonFilled = datetimeField?.dataMissingReason;
            if (controls[name].errors && controls[name].errors.required && !isMissingReasonFilled && !datetimeField.value) {
              invalid.push(name);
            }
          } else if (controls[name].errors && controls[name].errors.required) {
            invalid.push(name);
          }
        } else {
          if (controls[name].errors && (controls[name].errors.required !== true ||
            (name[0] === 's' && name.includes('service')))) {
            invalid.push(name);
          }
        }
      }
    });
    if (required && !hasRace) {
      invalid.push(AppConstants.raceBlackLinkId); // use first of race checkbox to raise required
    }
    if (countDelayreason < 4) {
      invalid = invalid.filter(item => !item.includes('delay-reason'));
    }
    return invalid;
  }

  // only for service information section when saving from/to Open status
  validateServiceInfo(formCollection: FormCollection) {
    for (const control of Object.keys(formCollection.form.controls)) {
      const item = formCollection.form.controls[control];
      if (item.status !== 'DISABLED' && !item.valid && formCollection.errorMsg.length === 0) {
        formCollection.data.forEach(data => {
          if (data.linkId === control) {
            formCollection.errorMsg = 'Please enter data for ' + data.label;
          }
        });
      }
    }
  }

  validateSection(formCollection: FormCollectionSet, section: string, formInvalid: string[], requiredFields) {
    const errorMsg = 'A value is required to save.';
    this.errorService.removeErrorForFormSection(section, formCollection, errorMsg);
    formCollection[section].data.forEach((field: BaseInput<any>) => {
      if (formInvalid?.includes(field.linkId)) {
        if (field.linkId.endsWith('-datetime') && requiredFields?.length > 0) {
          const inputField = formCollection[section].data.find (x => x.linkId === field.linkId);
          if(inputField.dateTimeErrorMsg.length > 0) {
            inputField.dateTimeErrorMsg = inputField.dateTimeErrorMsg; 
          } else {
            inputField.dateTimeErrorMsg = (inputField.errorMsg?.length > 0) ? '' : errorMsg; 
          }
          formCollection[section].form.get(field.linkId).setErrors({ infield: true });
        } else if (!field.disabled && field.show && !field.errorMsg && !field.invalid && !field.linkId.endsWith('-datetime')) {
          field.errorMsg = errorMsg;
          field.invalid = true;
        }
      }
    });
  }

  validateWholeEntry(formCollection: FormCollectionSet, formInvalid: string[], requiredFields) {
    Object.keys(formCollection).forEach((section: string) => {
      if (section !== 'waitTime') {
        this.validateSection(formCollection, section, formInvalid, requiredFields);
      }
    });
  }

  clearFieldOfRequiredError(formCollection: FormCollectionSet, section: string, formInvalid: string[]) {
    const errorMsg = 'A value is required to save.';
    formCollection[section].data.forEach((field: BaseInput<any>) => {
      if (formInvalid?.includes(field.linkId)) {
        if (field.invalid && field.errorMsg === errorMsg && (field.disabled || !field.show ||
          formCollection[section].form.get(field.linkId).value)) {
          field.errorMsg = '';
          field.invalid = false;
        }
      }
    });
  }

  clearFieldOfInFieldError(formCollection: FormCollectionSet, section: string, formInvalid: string[]) {
    formCollection[section].data.forEach((field: BaseInput<any>) => {
      if (formInvalid?.includes(field.linkId)) {
        if (field.invalid && formCollection[section].form.get(field.linkId).errors?.infield &&
          (field.disabled || !field.show || formCollection[section].form.get(field.linkId).value)) {
          field.errorMsg = '';
          field.invalid = false;
        }
      }
    });
  }

  getServiceQuestionnaire() {
    return new Promise<void> ((res, rej) => {
      if (!this.inputService.fullServiceAreaOptions || this.inputService.fullServiceAreaOptions.length === 0) {
        this.httpService.getQuestionnaireByIdentifier('Service').then((data: fhirR4.Bundle) => {
          if (+data.total > 0) {
            const questionnaire = data.entry[0].resource as fhirR4.Questionnaire;
            this.inputService.loadServiceFormCodes(questionnaire);
          }
          res();
        });
      } else {
        res();
      }
    });
  }
}
